/**
 * Batched fetch for every distinct `sender_id` present in a list of
 * `DBNotification` rows. Returns a `Map<userId, SenderProfile | null>`
 * keyed by user id — `null` means the notification expects a sender
 * (has a non-null `sender_id`) but the profile no longer exists
 * (Former Member / deleted account). The consumer uses this signal to
 * render a graceful "deleted user" placeholder instead of crashing.
 *
 * Why this lives in its own hook:
 *   Previously the Notification Centre rendered a blank rounded square
 *   for every row because `NotifRow` had no way to resolve the inviter's
 *   profile. Fetching inline (per row) would N+1 the API. This hook
 *   collapses every distinct sender_id in the current inbox + history
 *   into a single `profiles` query keyed on the sorted id list.
 *
 *   The query is `enabled` only when there's at least one sender to
 *   resolve, so signed-in users with an empty inbox never hit the table.
 *
 *   Errors are swallowed (`throwOnError` deliberately omitted) — a
 *   profile-resolution failure must NEVER crash the bell. The caller
 *   falls through to the themed-icon path when the Map is empty.
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { DBNotification } from "../types";

export interface SenderProfile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  email: string | null;
}

/**
 * Value semantics:
 *   • `SenderProfile` — profile resolved successfully.
 *   • `null`          — sender_id was requested but no profile exists
 *                        (account deleted → render "Former Member").
 *   • key absent      — not yet fetched, or no `sender_id` on the row.
 */
export type SenderMap = Map<string, SenderProfile | null>;

function uniqueSenderIds(notifs: DBNotification[]): string[] {
  const set = new Set<string>();
  for (const n of notifs) {
    if (n.sender_id) set.add(n.sender_id);
  }
  return Array.from(set).sort();
}

/**
 * Resolve every distinct sender_id in `notifs` to a lightweight profile
 * slice. Returns an empty `Map` until the query resolves, and `null`
 * entries for any id that was asked for but not returned by Supabase
 * (the classic "user deleted" scenario).
 */
export function useNotifSenders(notifs: DBNotification[]): SenderMap {
  const senderIds = useMemo(() => uniqueSenderIds(notifs), [notifs]);

  const { data } = useQuery<SenderProfile[]>({
    queryKey: ["notifications", "senders", senderIds],
    enabled: senderIds.length > 0,
    staleTime: 5 * 60_000,
    gcTime:    30 * 60_000,
    // Deliberate: do NOT `throwOnError`. A failure to resolve avatars
    // must never take the NotificationBell boundary down — the UI
    // gracefully falls through to themed icons if this query errors.
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, avatar_url, email")
        .in("id", senderIds);
      if (error) {
        // Log to console for dev, return empty so render stays safe.
        if (import.meta.env.DEV) {
          console.warn("[useNotifSenders] profile lookup failed:", error.message);
        }
        return [];
      }
      return (data ?? []) as SenderProfile[];
    },
  });

  return useMemo(() => {
    const map: SenderMap = new Map();

    // Seed every requested id with `null` first, so deleted users show
    // as "Former Member" instead of silently falling through to the
    // system-icon branch.
    for (const id of senderIds) map.set(id, null);

    // Overwrite with real profile rows where we got them back.
    for (const row of data ?? []) {
      if (row && typeof row.id === "string") map.set(row.id, row);
    }

    return map;
  }, [data, senderIds]);
}
